import type { Event } from '@cocoonspace/sdk-js/types/event'
import { Fragment, memo, useMemo } from 'react'
import { dateTimeUtils } from '@cocoonspace/shared/utils/date-time-utils'
import { BuildingIcon, CalendarClockIcon } from 'lucide-react'
import { AnimatePresence } from 'motion/react'

import { useViewPicker, ViewType } from './view-picker'

const DateHeader = ({ day }: { day: string }) => (
	<>
		<CalendarClockIcon />
		<div className='capitalize'>{day}</div>
	</>
)

const SpaceHeader = ({ title }: { title: string }) => (
	<>
		<BuildingIcon />
		<div className='capitalize'>{title}</div>
	</>
)

export const groupEventsBy = (events: Event[], groupBy: 'date' | 'space') => {
	return events.reduce(
		(acc, event) => {
			const key =
				groupBy === 'date'
					? dateTimeUtils(event.day).customFormat('dateLong')
					: event.space?.id

			acc[key as string] = acc[key as string] || []
			acc[key as string].push(event)
			return acc
		},
		{} as Record<string, Event[]>,
	)
}

export const ViewGroupBy = memo(
	({
		defaultView,
		events,
		render,
		withAnimatePresenceEvents = false,
	}: {
		defaultView?: ViewType
		events: Event[]
		render: (event: Event) => React.ReactNode
		/** When true, each group’s event list is wrapped in AnimatePresence (for motion enter/exit). */
		withAnimatePresenceEvents?: boolean
	}) => {
		const { view } = useViewPicker(defaultView)

		const groupBy = useMemo(() => {
			if (view === 'groupBySpace') return 'space'
			if (view === 'groupByDate') return 'date'
			return 'space'
		}, [view])

		const orderedEvents = useMemo(
			() => groupEventsBy(events, groupBy),
			[events, groupBy],
		)

		return (
			<div className='flex flex-col gap-4'>
				{Object.entries(orderedEvents).map(([day, events]) => (
					<div
						key={day}
						className='flex flex-row gap-4'
					>
						<div className='flex w-full flex-col gap-2'>
							<div className='flex items-center gap-2 font-bold'>
								{groupBy === 'date' && <DateHeader day={day} />}

								{groupBy === 'space' && (
									<SpaceHeader title={events[0].space?.title || ''} />
								)}
							</div>

							<div className='border-foreground ml-2 flex flex-col gap-4 border-l border-dashed pl-4'>
								{withAnimatePresenceEvents ? (
									<AnimatePresence
										initial={false}
										mode='popLayout'
									>
										{events.map((event) => render(event))}
									</AnimatePresence>
								) : (
									events.map((event) => (
										<Fragment key={event.id}>{render(event)}</Fragment>
									))
								)}
							</div>
						</div>
					</div>
				))}
			</div>
		)
	},
)

ViewGroupBy.displayName = 'ViewGroupBy'
